import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

// ─── FEATURE COMPASS ─────────────────────────────────────────────────────────
// One place that finds everything. Every feature in MFML — pages, panels,
// settings — listed with plain-language keywords, so "theme", "colors",
// "dark mode", "api key", "who's texting who" all land on the right thing.
//
// Open with the 🧭 button in the nav, or ⌘K / Ctrl+K anywhere.
//
// The parent (MainNav) builds the feature list — each entry:
//   { id, icon, name, desc, keywords, group, run() }
// so the compass itself stays dumb: filter, rank, render, run. Token-driven
// styling (var(--ds-*)) means it follows every theme, light and dark.

const norm = (s) => String(s || '').toLowerCase().normalize('NFKD');

function matches(feature, tokens) {
  if (!tokens.length) return true;
  const hay = norm(`${feature.name} ${feature.desc} ${feature.keywords}`);
  return tokens.every(tk => hay.includes(tk));
}

// Rank: name-prefix beats name-contains beats keyword/desc match.
function score(feature, tokens) {
  if (!tokens.length) return 0;
  const name = norm(feature.name);
  let s = 0;
  for (const tk of tokens) {
    if (name.startsWith(tk)) s += 3;
    else if (name.includes(tk)) s += 2;
    else s += 1; // matched in keywords/desc (matches() already guaranteed it)
  }
  return s;
}

export default function FeatureCompass({ open, onClose, features, isMobile }) {
  const [query, setQuery] = useState('');
  const [selIdx, setSelIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset + focus on every open.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelIdx(0);
      // Focus after the overlay paints (autofocus is unreliable in portals).
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const tokens = useMemo(() => norm(query).split(/\s+/).filter(Boolean), [query]);

  const visible = useMemo(() => {
    const hits = (features || []).filter(f => matches(f, tokens));
    if (tokens.length) hits.sort((a, b) => score(b, tokens) - score(a, tokens));
    return hits;
  }, [features, tokens]);

  // Group for display (only when not searching — search shows a flat ranked list).
  const grouped = useMemo(() => {
    if (tokens.length) return [{ group: null, items: visible }];
    const order = [];
    const byGroup = new Map();
    for (const f of visible) {
      if (!byGroup.has(f.group)) { byGroup.set(f.group, []); order.push(f.group); }
      byGroup.get(f.group).push(f);
    }
    return order.map(g => ({ group: g, items: byGroup.get(g) }));
  }, [visible, tokens]);

  const runFeature = useCallback((f) => {
    onClose?.();
    // Let the overlay close before the action mounts anything heavy.
    setTimeout(() => { try { f.run(); } catch (e) { console.error('[Compass] action failed:', e); } }, 0);
  }, [onClose]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelIdx(i => Math.min(i + 1, visible.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const f = visible[selIdx];
      if (f) runFeature(f);
    }
  }, [visible, selIdx, onClose, runFeature]);

  // Keep the selected row in view while arrowing.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selIdx]);

  // Clamp selection when the filter shrinks the list.
  useEffect(() => { if (selIdx > visible.length - 1) setSelIdx(0); }, [visible.length, selIdx]);

  if (!open) return null;

  let flatIdx = -1;

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Feature compass — find anything"
      style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: isMobile ? 'stretch' : 'flex-start', justifyContent: 'center',
        padding: isMobile ? 0 : '10vh 16px 16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="baroque-frame"
        style={{
          width: '100%', maxWidth: isMobile ? 'none' : '560px',
          maxHeight: isMobile ? '100dvh' : '70vh',
          height: isMobile ? '100dvh' : 'auto',
          display: 'flex', flexDirection: 'column',
          background: 'var(--ds-glass-strong, rgba(16,12,16,0.89))',
          backdropFilter: 'blur(18px) saturate(1.3)', WebkitBackdropFilter: 'blur(18px) saturate(1.3)',
          borderRadius: isMobile ? 0 : 'var(--ds-r-lg, 20px)',
          overflow: 'hidden',
        }}
      >
        {/* Header + search */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--ds-line-soft, #251c26)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span aria-hidden="true" style={{ fontSize: '20px' }}>🧭</span>
            <span style={{ fontFamily: 'var(--ds-font-display)', fontWeight: 700, fontSize: '15px', letterSpacing: '2px', color: 'var(--ds-gold, #c4a882)', textTransform: 'uppercase' }}>Compass</span>
            <button onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--ds-ink-faint)', fontSize: '16px', cursor: 'pointer', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          </div>
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelIdx(0); }}
            onKeyDown={onKeyDown}
            placeholder="Find anything — try “theme”, “notifications”, “call”…"
            aria-label="Search features"
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: '8px',
              background: 'var(--ds-input, #0a070a)', color: 'var(--ds-ink-hi, #f5efe4)',
              border: '1px solid var(--ds-line, #372b39)', borderRadius: 'var(--ds-r-sm, 10px)',
              padding: '12px 14px', fontSize: '16px', outline: 'none', fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Results */}
        <div ref={listRef} style={{ overflowY: 'auto', padding: '8px', flex: 1 }}>
          {visible.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--ds-ink-faint)', fontFamily: 'var(--ds-font-body)', fontSize: '15px' }}>
              Nothing matches “{query}” — try a simpler word.
            </div>
          )}
          {grouped.map(({ group, items }) => (
            <div key={group || 'results'}>
              {group && (
                <div style={{ padding: '10px 10px 4px', fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--ds-ink-faint)', fontFamily: 'var(--ds-font-ui)' }}>{group}</div>
              )}
              {items.map((f) => {
                flatIdx += 1;
                const idx = flatIdx;
                const selected = idx === selIdx;
                return (
                  <button
                    key={f.id}
                    data-idx={idx}
                    onClick={() => runFeature(f)}
                    onMouseEnter={() => setSelIdx(idx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
                      textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box',
                      background: selected ? 'var(--ds-accent-a15, rgba(198,40,40,0.09))' : 'none',
                      border: 'none', borderLeft: selected ? '2px solid var(--ds-accent, #c62828)' : '2px solid transparent',
                      borderRadius: 'var(--ds-r-sm, 10px)',
                      padding: '10px 12px', minHeight: '52px',
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: '20px', flexShrink: 0, width: '28px', textAlign: 'center' }}>{f.icon}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--ds-ink-hi, #f5efe4)', fontFamily: 'var(--ds-font-ui)' }}>{f.name}</span>
                      <span style={{ display: 'block', fontSize: '12px', color: 'var(--ds-ink-low, #9d9182)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer hint (desktop only — mobile has no keyboard) */}
        {!isMobile && (
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--ds-line-soft, #251c26)', fontSize: '11px', color: 'var(--ds-ink-faint)', display: 'flex', gap: '14px', flexShrink: 0 }}>
            <span>↑↓ choose</span><span>↵ go</span><span>esc close</span>
            <span style={{ marginLeft: 'auto' }}>⌘K opens this anywhere</span>
          </div>
        )}
      </div>
    </div>
  );
}
